import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCheckCircle,
  FaAward,
  FaUsers,
  FaShieldAlt,
  FaRegLightbulb,
  FaRegHandshake,
  FaCertificate,
  FaClipboardCheck,
  FaSearch,
  FaFileSignature,
  FaGlobe,
  FaHospital,
  FaGraduationCap,
  FaIndustry,
  FaLaptopCode,
  FaUtensils,
  FaShip
} from "react-icons/fa";
import "../Style/About.css";

const testimonials = [
  {
    name: "Operations Manager",
    company: "Hospital Sector",
    text: "The process was smooth, and the team explained every step clearly. Our audit was much easier than expected.",
    stars: 5,
    img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=120&h=120&q=80"
  },
  {
    name: "Admin Director",
    company: "Education Sector",
    text: "Great documentation support and training. They made ISO implementation practical for our staff.",
    stars: 5,
    img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=120&h=120&q=80"
  },
  {
    name: "Project Head",
    company: "Construction Sector",
    text: "Very professional and responsive team. We felt supported from gap analysis to certification.",
    stars: 5,
    img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=120&h=120&q=80"
  }
];

const timelineSteps = [
  { icon: <FaSearch />, title: "Gap Analysis", text: "We review your current system and identify what needs improvement." },
  { icon: <FaFileSignature />, title: "Documentation", text: "We prepare policies, procedures, forms, and records." },
  { icon: <FaClipboardCheck />, title: "Internal Audit", text: "We verify readiness and resolve issues before certification." },
  { icon: <FaCertificate />, title: "Certification", text: "We guide you through the final certification journey." }
];

const consultingApproaches = [
  {
    icon: <FaRegLightbulb />,
    title: "Tailored Implementation",
    text: "We avoid templates that don't fit, designing management systems customized directly to your operational workflow."
  },
  {
    icon: <FaRegHandshake />,
    title: "Hands-On Partnership",
    text: "We work alongside your internal teams rather than dictating from a distance, driving smooth adoption."
  },
  {
    icon: <FaShieldAlt />,
    title: "Risk-Based Mindset",
    text: "We focus on proactive risk mitigation to safeguard your assets, compliance profile, and continuous growth."
  },
  {
    icon: <FaCheckCircle />,
    title: "Sustainable Compliance",
    text: "We design lightweight, efficient structures that your staff can easily maintain long after our consultants leave."
  }
];

const whyChooseUsPoints = [
  {
    icon: <FaAward />,
    title: "Proven Track Record",
    text: "Over a decade of successful ISO implementations and audit passes across multiple highly regulated industries."
  },
  {
    icon: <FaUsers />,
    title: "Expert Practitioners",
    text: "Certified lead auditors and industry specialists who bring real-world operational insight, not just theoretical advice."
  },
  {
    icon: <FaShieldAlt />,
    title: "100% Audit Success Guarantee",
    text: "We stand by our work, providing dedicated support through every stage until your organization receives its official certificate."
  },
  {
    icon: <FaRegHandshake />,
    title: "End-to-End Guidance",
    text: "From initial gap analysis and customized documentation to internal training and final registrar audit support."
  }
];

const industriesServed = [
  { 
    icon: <FaHospital />, 
    title: "Healthcare & Hospitals", 
    desc: "Ensuring safety standards, patient data security, and stringent clinical protocols.",
    img: "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=600&q=80"
  },
  { 
    icon: <FaGraduationCap />, 
    title: "Education & Academics", 
    desc: "Structuring academic institutions for excellence, safety, and efficient administration.",
    img: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=600&q=80"
  },
  { 
    icon: <FaIndustry />, 
    title: "Manufacturing & Production", 
    desc: "Optimizing supply lines, process standardization, and waste reduction benchmarks.",
    img: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=600&q=80"
  },
  { 
    icon: <FaLaptopCode />, 
    title: "IT & Digital Services", 
    desc: "Securing information systems, data infrastructure, and service management lifecycles.",
    img: "https://plus.unsplash.com/premium_photo-1661877447518-8f54d8bdbadb?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8SVQlMjAlMjYlMjBEaWdpdGFsJTIwU2VydmljZXN8ZW58MHx8MHx8fDA%3D"
  },
  { 
    icon: <FaUtensils />, 
    title: "Hospitality & Food Services", 
    desc: "Implementing food safety hygiene matrices, guest experience tracking, and sanitation standards.",
    img: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=600&q=80"
  },
  { 
    icon: <FaShip />, 
    title: "Marine & Logistics", 
    desc: "Navigating international maritime regulations, freight supply lines, and transport safety.",
    img: "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&w=600&q=80"
  }
];

function About() {
  const navigate = useNavigate();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4500);
    return () => clearInterval(timer);
  }, []);

  const testimonial = testimonials[currentTestimonial];

  const handleStandardClick = (id) => {
    navigate(`/certifications/${id}`);
    window.scrollTo(0, 0);
  };

  return (
    <section className="about-page">
      <div className="about-page-bg-shield"></div>
      <div className="about-page-radial-vignette"></div>

      {/* Floating Animated Background Orbs */}
      <div className="anim-orb orb-1"></div>
      <div className="anim-orb orb-2"></div>

      <div className="about-hero">
        <div className="container about-hero-grid">
          <div className="about-hero-content reveal-on-scroll">
            <span className="section-tag tag-yellow">ABOUT ASCENT MALDIVES</span>
            <h1>Global ISO Certification &amp; Consulting Experts</h1>
            <p>
              Companies around the world depend on <strong className="highlight-red">Ascent INSPECTA MALDIVES</strong> to ensure the Quality, safety, and compliance of their products, processes, and systems. Over 10 years of experience in the implementation of Standards and Controls means our ISO Consultants are proven professionals.
            </p>
            <div className="about-hero-actions">
              <button className="primary-btn ripple-effect">Get Consultation</button>
              <button className="secondary-btn">Our Services</button>
            </div>
          </div>
          <div className="about-hero-image reveal-on-scroll delay-2">
            <div className="image-frame-protection floating-float-anim">
              <img src="https://images.unsplash.com/photo-1606857521015-7f9fcf423740?auto=format&fit=crop&w=800&q=80" alt="Consulting team" />
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Who We Are Section */}
        <div className="who-we-are-section reveal-on-scroll" style={{ padding: "40px 0" }}>
          <div className="global-reach-card">
            <div className="reach-content">
              <span className="section-tag tag-red" style={{ marginBottom: "12px" }}>WHO WE ARE</span>
              <h3>Excellence in ISO Compliance &amp; Inspection</h3>
              <p>
                Ascent INSPECTA MALDIVES is a premier provider of ISO certification consulting, management system integration, and global compliance engineering. We bridge the gap between rigorous international standards and practical, everyday business operations. 
              </p>
              <p>
                Our multidisciplinary team of seasoned lead auditors and compliance specialists work hand-in-hand with enterprises of all sizes—from ambitious local startups to large multinational corporations—ensuring they achieve sustainable growth, mitigate operational risks, and secure world-class accreditation seamlessly.
              </p>
            </div>
            <div className="reach-media-side">
              <div className="reach-image-wrapper" style={{ height: "300px" }}>
                <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSbi6FCJPd7uHGr6i3J_oVQflzfPqegEwnLYuklCVZA6A&s=10" alt="Professional business team meeting" />
                <div className="reach-glow-badge">
                  <FaShieldAlt className="spin-slow" />
                  <span>Certified Excellence</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose Us Section */}
        <div className="why-choose-us-section reveal-on-scroll" style={{ padding: "40px 0" }}>
          <div className="section-heading">
            <span className="section-tag tag-yellow">THE ASCENT ADVANTAGE</span>
            <h2>Why Choose Us</h2>
            <p className="custom-heading-desc">Discover what makes Ascent INSPECTA the preferred compliance partner for industry leaders.</p>
          </div>
          <div className="approach-grid">
            {whyChooseUsPoints.map((item, index) => (
              <div className="approach-card card-hover-zoom" key={item.title}>
                <div className="approach-number">0{index + 1}</div>
                <div className="approach-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
                <div className="card-shine-fx"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Our Global Reach Section */}
        <div className="global-reach-section reveal-on-scroll">
          <div className="section-heading">
            <span className="section-tag tag-yellow">INTERNATIONAL FOOTPRINT</span>
            <h2>Our Global Reach</h2>
            <p className="custom-heading-desc">Bridging local enterprises with world-class international standards across borders and time zones.</p>
          </div>
          <div className="global-reach-card">
            <div className="reach-media-side">
              <div className="reach-image-wrapper">
                <img src="https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?auto=format&fit=crop&w=700&q=80" alt="Global reach network map" />
                <div className="reach-glow-badge">
                  <FaGlobe className="spin-slow" />
                  <span>Worldwide Network</span>
                </div>
              </div>
            </div>
            <div className="reach-content">
              <h3>Trusted Across Multiple Jurisdictions</h3>
              <p>
                Headquartered with strong regional execution capabilities, Ascent INSPECTA leverages a worldwide network of accredited auditors, compliance engineers, and industry specialists. Whether scaling operations across South Asia, the Middle East, or international trade nodes, our frameworks translate seamlessly into local regulatory compliances and global certifications.
              </p>
              <div className="reach-stats-row">
                <div className="reach-stat-item">
                  <strong>10+</strong>
                  <span>Years Experience</span>
                </div>
                <div className="reach-stat-item">
                  <strong>500+</strong>
                  <span>Global Projects</span>
                </div>
                <div className="reach-stat-item">
                  <strong>100%</strong>
                  <span>Audit Success Track</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Consulting Approach Section */}
        <div className="consulting-approach-section reveal-on-scroll">
          <div className="section-heading">
            <span className="section-tag tag-yellow">METHODOLOGY</span>
            <h2>Our Consulting Approach</h2>
            <p className="custom-heading-desc">How we partner with your organization to turn complex requirements into clear competitive advantages.</p>
          </div>
          <div className="approach-grid">
            {consultingApproaches.map((item, index) => (
              <div className="approach-card card-hover-zoom" key={item.title}>
                <div className="approach-number">0{index + 1}</div>
                <div className="approach-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
                <div className="card-shine-fx"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Industries We Serve Section with Background Images */}
        <div className="industries-section reveal-on-scroll">
          <div className="section-heading">
            <span className="section-tag tag-red">SECTORS</span>
            <h2>Industries We Serve</h2>
            <p className="custom-heading-desc">Deep domain competence tailored to unique sector-specific risks, workflows, and compliance mandates.</p>
          </div>
          <div className="industries-grid">
            {industriesServed.map((ind) => (
              <div className="industry-card-img" key={ind.title}>
                <div className="industry-bg-image" style={{ backgroundImage: `url(${ind.img})` }}></div>
                <div className="industry-card-overlay"></div>
                <div className="industry-content-wrap">
                  <div className="industry-icon-pill">{ind.icon}</div>
                  <h3>{ind.title}</h3>
                  <p>{ind.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="about-timeline">
          <div className="timeline-heading reveal-on-scroll">
            <span className="section-tag tag-yellow">PROCESS</span>
            <h2>ISO Certification Timeline</h2>
          </div>
          <div className="timeline-grid">
            {timelineSteps.map((step, index) => (
              <div className={`timeline-card card-hover-zoom`} key={step.title}>
                <div className="step-count-flag">0{index + 1}</div>
                <div className="timeline-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Remastered Competence Dashboard - Stacked Up/Down Dual Image Layout */}
        <div className="competence-dashboard reveal-on-scroll">
          <div className="competence-stacked-layout">
            
            {/* Top Text Block */}
            <div className="competence-top-content">
              <span className="mini-accent-tag">COMPREHENSIVE COMPETENCE</span>
              <h3>Global Standards &amp; Product Markings Supported</h3>
              <p>Unmatched expertise making Ascent INSPECTA a complete, one-stop solution provider.</p>
            </div>

            {/* Middle Dual Stacked Images Banner */}
            <div className="competence-stacked-images-row">
              <div className="competence-stacked-img-box">
                <img src="https://media.istockphoto.com/id/2150522257/photo/businessman-holding-and-showing-the-best-quality-assurance-with-five-stars-iso-certification.webp?a=1&b=1&s=612x612&w=0&k=20&c=OMLSJ6_hQFrZfaA2TsxPe-umBhYDgVU9b8ac5cxMij0=" alt="Inspection process" />
              </div>
              <div className="competence-stacked-img-box">
                <img src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=900&q=80" alt="Compliance documentation" />
                <div className="competence-floating-badge">
                  <FaCertificate className="spin-slow" />
                  <span>Accredited Standards</span>
                </div>
              </div>
            </div>

            {/* Bottom Data Pills Block */}
            <div className="competence-bottom-data">
              <div className="standards-group">
                <h4>ISO Management Standards (Click to view standard)</h4>
                <div className="pills-grid">
                  {[
                    { label: "ISO 9001:2015 QMS", id: "iso-9001" },
                    { label: "ISO 14001:2015 EMS", id: "iso-14001" },
                    { label: "ISO 45001:2018 OH&S", id: "iso-45001" },
                    { label: "ISO 50001:2018 Energy", id: "iso-50001" },
                    { label: "ISO 55001:2014 Asset Mgmt", id: "iso-55001" },
                    { label: "ISO 41001:2018 Facility", id: "iso-41001" },
                    { label: "ISO 22000:2018 Food Safety", id: "iso-22000" },
                    { label: "ISO 13485:2016 Medical Devices", id: "iso-13485" },
                    { label: "ISO 20000-1:2018 IT Service", id: "iso-20000-1" },
                    { label: "ISO/IEC 27001:2022 ISMS", id: "iso-27001" },
                    { label: "ISO 28000:2022 Supply Chain", id: "iso-28000" },
                    { label: "ISO 22301:2019 Business Continuity", id: "iso-22301" },
                    { label: "ISO 37001:2016 Anti-Bribery", id: "iso-37001" },
                    { label: "ISO 31000 Risk Mgmt", id: "iso-31000" },
                    { label: "ISO 21001:2018 Educational", id: "iso-21001" },
                    { label: "ISO 39001:2012 Road Traffic", id: "iso-39001" },
                    { label: "ISO 26000 Social Responsibility", id: "iso-26000" },
                    { label: "ISO 20121:2024 Event Mgmt", id: "iso-20121" },
                    { label: "ISO 10002:2018 Complaint Mgmt", id: "iso-10002" },
                    { label: "ISO 10001:2018 Customer Satisfaction", id: "iso-10001" }
                  ].map((std) => (
                    <button 
                      type="button"
                      key={std.id} 
                      onClick={() => handleStandardClick(std.id)}
                      className="competence-pill iso-pill clickable-pill"
                      style={{ cursor: "pointer", background: "rgba(245, 158, 11, 0.12)", border: "none", font: "inherit" }}
                    >
                      {std.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="standards-group">
                <h4>Global Product Markings</h4>
                <div className="pills-grid">
                  {["CE Marking", "UL Marking", "ISI Marking", "SASO Marking", "FCC Marking", "CCC Marking", "Gost R Marking"].map((mark) => (
                    <span key={mark} className="competence-pill mark-pill">{mark}</span>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default About;